import type { CSSProperties } from 'react';
import { Icon, type IconName } from '../primitives/Icon.tsx';
import './kpi-card.css';

export interface KpiCardProps {
  label: string;
  value: string;
  /** "orders", "%", "at 12%" - grey, beside the bold value. */
  unit?: string;
  meta?: string;
  iconName?: IconName;
  /** Tints the icon badge. Colour is meaning here, never decoration. */
  tint?: 'cyan' | 'pink' | 'amber' | 'ink';
  /** Colours positive context in cyan ink. */
  metaTone?: 'up' | 'flat';
  /** Marks a figure GA4 is still settling, or one derived from sampled data. */
  provisional?: boolean;
  style?: CSSProperties;
}

/**
 * One headline figure.
 *
 * The design system's metric pattern: value → unit → context line. The arrow is a
 * glyph plus a word, never colour alone — "↑ 18%" in green and "↓ 0.3" in red are the
 * same shape to anyone who cannot separate them.
 *
 * `provisional` rides on the context line rather than in a pill of its own. A fourth
 * row makes one tile taller than its neighbours, and the grid then stretches all four.
 */
export function KpiCard({
  label,
  value,
  unit,
  meta,
  iconName,
  tint = 'cyan',
  metaTone = 'flat',
  provisional,
  style,
}: KpiCardProps) {
  const context = [meta, provisional ? 'still settling' : null].filter(Boolean).join(' - ');

  return (
    <section className="kpiCard" style={style}>
      <header className="kpiCardHead">
        <span className="kpiCardLabel">{label}</span>
        {iconName ? (
          <span className="kpiCardIcon" data-accent={tint} aria-hidden="true">
            <Icon name={iconName} size={16} />
          </span>
        ) : null}
      </header>

      <p className="kpiCardValue">
        <span className="typeMetric">{value}</span>
        {unit ? <span className="typeUnit">{unit}</span> : null}
      </p>

      {context ? (
        <p className="kpiCardMeta" data-trend={metaTone}>
          {context}
        </p>
      ) : null}
    </section>
  );
}
